import { useEffect, useMemo, useState } from "react";

import { Stack } from "@mui/material";

import toast from "react-hot-toast";

import { MeetingQuestion, MeetingSurvey } from "../../../types";
import { useUpdateMeetingSurvey } from "../../../store";
import FillSurveyQuestion from "./fill-survey-question";

interface MeetingSurveyQuestionsProps {
  meetingId: string;
  userId: string,
  joinCode: string,
  questionIdList: string[];
  survey?: MeetingSurvey,
}

const getInitialState = (
  survey: MeetingSurvey | undefined,
  questionIds: string[],
) => {

  const questions: (MeetingQuestion | null)[] = [];

  for (let i = 0; i < questionIds.length; i++) {
    questions.push(null);
  }

  if (!survey) {
    return questions;
  }

  for (let i = 0; i < questionIds.length; i++) {
    questions[i] =
      survey.responses.find((q) => q.questionId === questionIds[i]) || null;
  }

  return questions;
};

const MeetingSurveyQuestions: React.FC<MeetingSurveyQuestionsProps> = ({
  meetingId,
  userId,
  joinCode,
  questionIdList,
  survey,
}) => {

  const [pendingQuestionIds, setPendingQuestionIds] = useState<Record<string, boolean>>({})

  const [responses, setResponses] =
    useState<MeetingSurvey | undefined>(survey);

  const { mutateAsync: updateSurvey } = useUpdateMeetingSurvey()

  const setQuestionPending = (questionId: string, pending: boolean) => {
    setPendingQuestionIds((current) => {
      if (!pending) {
        const next = { ...current }
        delete next[questionId]
        return next
      }

      return {
        ...current,
        [questionId]: true,
      }
    })
  }

  const onSurveyUpdated = (question: MeetingQuestion) => {

    const survey: MeetingSurvey = {
      userId,
      responses: [question]
    }

    setQuestionPending(question.questionId, true)

    void updateSurvey({
      survey,
      meetingId,
      userId,
      joinCode
    })
      .then((data) => {
        toast.success("Response recorded")
        setResponses(data || undefined)
      })
      .catch((error) => {
        console.error("Failed to record response:", error)
        toast.error(`Failed record response`)

        if (!responses) {
          setResponses({
            userId,
            responses: []
          })
        }
      })
      .finally(() => {
        setQuestionPending(question.questionId, false)
      })
  }

  const surveyResponses = useMemo(() => {
    return getInitialState(responses, questionIdList);
  }, [responses, questionIdList])


  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!responses) {
        e.preventDefault();
        e.returnValue = ""; // Required for Chrome
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [responses]);

  return (
    <Stack justifyContent="center" alignItems="center" spacing={2}>
      {surveyResponses && surveyResponses.map((_, index) => (
        <FillSurveyQuestion
          key={questionIdList[index]}
          pending={Boolean(pendingQuestionIds[questionIdList[index]])}
          questionNumber={index + 1}
          questionId={questionIdList[index]}
          startResponse={surveyResponses[index]?.surveyResponse}
          onSurveyUpdated={onSurveyUpdated} />
      ))}
    </Stack>
  );
};

export default MeetingSurveyQuestions;
